/* eslint-disable @shopliftr/angular/dialog-service-conventions -- Hotkey Service follows its own pattern */
import { DOCUMENT } from "@angular/common";
import {
    DestroyRef, Injectable, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { EventManager } from "@angular/platform-browser";
import {
    MatDialog, MatDialogRef
} from "@angular/material/dialog";
import {
    EMPTY, fromEvent, Observable, of, Subject, Subscription
} from "rxjs";
import {
    debounceTime, filter, finalize, mergeMap, takeUntil, tap
} from "rxjs/operators";
import { Platform } from "./enums/platform";
export { Platform } from "./enums/platform";
import {
    AllowInElement, Hotkey, HotkeyCallback, IHotkeyGroup, IHotkeyOptions
} from "./interfaces/hotkeys";
export type {
    AllowInElement, Hotkey, HotkeyCallback, IHotkeyGroup, IHotkeyOptions
} from "./interfaces/hotkeys";

/**
 * Internal summary of a hotkey registration.
 */
interface HotkeySummary {
    /** The hotkey configuration */
    hotkey: Hotkey;

    /** Subject for emitting hotkey events */
    subject$: Subject<Hotkey>;
}

/**
 * Internal summary of sequence hotkey registrations for an element.
 */
interface SequenceSummary {
    /** Subscription to keyboard events */
    subscription: Subscription;

    /** Observable stream of hotkey events */
    observer$: Observable<Hotkey>;

    /** Map of key sequences to their hotkey summaries */
    hotkeyMap: Map<string, HotkeySummary>;
}

/**
 * Service for managing keyboard shortcuts and hotkey sequences.
 *
 * Provides functionality to register single hotkeys and multi-key sequences,
 * with support for platform-specific key normalization, help menu integration,
 * and element-specific targeting.
 *
 * @example
 * ```typescript
 * // Register a simple hotkey
 * this.hotkeysService.addShortcut({
 *   keys: 'ctrl.s',
 *   description: 'Save document',
 *   group: 'File Operations'
 * }).subscribe(() => this.save());
 *
 * // Register a key sequence
 * this.hotkeysService.addSequenceShortcut({
 *   keys: 'g>h',
 *   description: 'Go to home',
 *   group: 'Navigation'
 * }).subscribe(() => this.goHome());
 * ```
 */
@Injectable({ providedIn: "root" })
export class HotkeysService {
    /** Angular event manager for DOM event handling */
    private readonly _eventManager = inject(EventManager);

    /** Document reference for DOM operations */
    private readonly _document = inject<Document>(DOCUMENT);

    /** Map of normalized key strings to hotkey configurations */
    private readonly _hotkeys = new Map<string, Hotkey>();

    /** Subject for disposing non-dialog hotkey subscriptions */
    private readonly _dispose$ = new Subject<string>();

    /** Subject for disposing dialog hotkey subscriptions */
    private readonly _dialogDispose$ = new Subject<string>();

    /** Default configuration options for hotkeys */
    private readonly _defaults: Omit<IHotkeyOptions, "destroyRef">;

    /** Array of global hotkey callback functions */
    private _callbacks: Array<HotkeyCallback> = [];

    /** Map of elements to their sequence hotkey configurations */
    private readonly _sequenceMaps = new Map<HTMLElement, SequenceSummary>();

    /** Debounce time in milliseconds for key sequence detection */
    private _sequenceDebounce: number = 250;

    /** MatDialog for tracking open/close events */
    private readonly _matDialog = inject(MatDialog);

    /** AppService for broadcasting the close-all-panels signal */
    private readonly _app = inject(COMMON_APP_SERVICE);

    /** Count of currently open dialogs */
    private _openDialogCount = 0;

    /** Map of normalized hotkey keys to their dialog hotkey config and owning dialog ref */
    private readonly _dialogHotkeys = new Map<string, {
        hotkey: Hotkey;
        dialogRef: MatDialogRef<unknown>;
    }>();

    /** Map of ALL dialog-owned hotkey keys (single and sequence) to their owning dialog ref for cleanup */
    private readonly _dialogOwners = new Map<string, MatDialogRef<unknown>>();

    /** Map of elements to their dialog-scoped sequence hotkey configurations */
    private readonly _dialogSequenceMaps = new Map<HTMLElement, SequenceSummary>();

    /** The reference for when this service is destroyed */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Initializes the HotkeysService with default configuration.
     *
     * Sets up default options for hotkey registration including:
     * - keydown trigger event
     * - document element as default target
     * - help menu visibility enabled
     * - preventDefault enabled
     */
    constructor() {

        this._defaults = {
            trigger: "keydown",
            allowIn: [],
            element: this._document.documentElement,
            group: undefined,
            description: undefined,
            showInHelpMenu: true,
            preventDefault: true,
            closeAllPanels: true
        };

        this._matDialog.afterOpened.pipe(
            mergeMap((dialogRef) => {
                this._openDialogCount++;
                return dialogRef.afterClosed().pipe(
                    tap(() => {
                        this._openDialogCount--;
                        const keysToRemove: Array<string> = [];
                        this._dialogOwners.forEach((ref, keys) => {
                            if (ref === dialogRef) {
                                keysToRemove.push(keys);
                            }
                        });
                        keysToRemove.forEach((keys) => {
                            this._removeDialogShortcuts(keys);
                        });
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    /**
     * Retrieves all registered hotkeys including single keys and sequences.
     *
     * @returns Array of all registered hotkey configurations
     */
    getHotkeys(): Array<Hotkey> {
        const allSequenceMaps = [...this._sequenceMaps.values(), ...this._dialogSequenceMaps.values()];
        const sequenceKeys = allSequenceMaps
            .flatMap((s) => [...s.hotkeyMap.values()])
            .map((h) => h.hotkey);

        const dialogHotkeyConfigs = Array.from(this._dialogHotkeys.values()).map((entry) => entry.hotkey);

        return Array.from(this._hotkeys.values())
            .concat(dialogHotkeyConfigs)
            .concat(sequenceKeys);
    }

    /**
     * Gets hotkeys grouped by category for display in help menu.
     *
     * Only includes hotkeys with showInHelpMenu set to true.
     * Keys are normalized for the current platform.
     *
     * @returns Array of hotkey groups with normalized key strings
     */
    getShortcuts(): Array<IHotkeyGroup> {
        const hotkeys = this.getHotkeys();
        const groups: Array<IHotkeyGroup> = [];

        for (const hotkey of hotkeys) {
            if (!hotkey.showInHelpMenu) {
                continue;
            }

            let group = groups.find((g) => g.group === hotkey.group);
            if (!group) {
                group = {
                    group: hotkey.group,
                    hotkeys: []
                };
                groups.push(group);
            }

            const normalizedKeys = this._normalizeKeys(hotkey.keys, this.hostPlatform());
            group.hotkeys.push({
                keys: normalizedKeys,
                description: hotkey.description
            });
        }

        return groups;
    }

    /**
     * Registers a multi-key sequence hotkey (e.g., 'g>h' for 'g' then 'h').
     *
     * Sequences are detected using a debounce mechanism. Keys must be pressed
     * within the debounce window to be considered part of the sequence.
     *
     * @param options - Hotkey configuration with sequence keys
     * @returns Observable that emits when the sequence is completed
     *
     * @example
     * ```typescript
     * // Register 'g' followed by 'h' sequence
     * this.hotkeysService.addSequenceShortcut({
     *   keys: 'g>h',
     *   description: 'Go to home'
     * }).subscribe(() => this.navigateHome());
     * ```
     */
    addSequenceShortcut$(options: Hotkey): Observable<Hotkey> {
        const getSequenceObserver = (element: HTMLElement, eventName: string): Observable<Hotkey> => {
            let sequence = "";
            return fromEvent<KeyboardEvent>(element, eventName).pipe(
                tap(
                    (e) =>
                        (sequence = `${sequence}${sequence ? ">" : ""}${e.ctrlKey ? "control." : ""}${e.altKey ? "alt." : ""}${
                            e.shiftKey ? "shift." : ""
                        }${e.key}`),
                ),
                debounceTime(this._sequenceDebounce),
                mergeMap(() => {
                    const resultSequence = sequence;
                    sequence = "";
                    const summary = this._sequenceMaps.get(element);
                    if (summary.hotkeyMap.has(resultSequence)) {
                        const hotkeySummary = summary.hotkeyMap.get(resultSequence);
                        if (this._openDialogCount > 0) {
                            return EMPTY;
                        }
                        if (mergedOptions.closeAllPanels !== false) {
                            this._app.closeAllPanels();
                        }
                        hotkeySummary.subject$.next(hotkeySummary.hotkey);
                        return of(hotkeySummary.hotkey);
                    }
                    else {
                        return EMPTY;
                    }
                }),
            );
        };

        const mergedOptions = {
            ...this._defaults,
            ...options
        };
        const normalizedKeys = this._normalizeKeys(mergedOptions.keys, this.hostPlatform());

        const getSequenceCompleteObserver = (): Observable<Hotkey> => {
            const hotkeySummary = {
                subject$: new Subject<Hotkey>(),
                hotkey: mergedOptions
            };

            if (this._sequenceMaps.has(mergedOptions.element)) {
                const sequenceSummary = this._sequenceMaps.get(mergedOptions.element);

                if (sequenceSummary.hotkeyMap.has(normalizedKeys)) {
                    console.error("Duplicated shortcut");
                    return of(null);
                }

                sequenceSummary.hotkeyMap.set(normalizedKeys, hotkeySummary);
            }
            else {
                const observer$ = getSequenceObserver(mergedOptions.element, mergedOptions.trigger);
                const subscription = observer$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe();

                const hotkeyMap = new Map<string, HotkeySummary>([[normalizedKeys, hotkeySummary]]);
                const sequenceSummary = {
                    subscription,
                    observer$,
                    hotkeyMap
                };
                this._sequenceMaps.set(mergedOptions.element, sequenceSummary);
            }

            if (mergedOptions.destroyRef) {
                mergedOptions.destroyRef.onDestroy(() => {
                    this.removeShortcuts(normalizedKeys);
                });
            }
            return hotkeySummary.subject$.asObservable();
        };

        return getSequenceCompleteObserver().pipe(
            filter((hotkey) => !this._targetIsExcluded(hotkey.allowIn)),
            tap((hotkey) => this._callbacks.forEach((cb) => cb(hotkey, normalizedKeys, hotkey.element))),
            takeUntil<Hotkey>(this._dispose$.pipe(filter((v) => v === normalizedKeys))),
            finalize(() => this.removeShortcuts(normalizedKeys)),
        );
    }

    /**
     * Registers a single hotkey combination (e.g., 'ctrl.s', 'alt.f4').
     *
     * @param options - Hotkey configuration
     * @returns Observable that emits keyboard events when hotkey is triggered
     *
     * @example
     * ```typescript
     * // Register Ctrl+S hotkey
     * this.hotkeysService.addShortcut({
     *   keys: 'ctrl.s',
     *   description: 'Save document',
     *   preventDefault: true
     * }).subscribe(() => this.saveDocument());
     * ```
     */
    addShortcut$(options: Hotkey): Observable<KeyboardEvent> {
        const mergedOptions = {
            ...this._defaults,
            ...options
        };
        const normalizedKeys = this._normalizeKeys(mergedOptions.keys, this.hostPlatform());

        if (this._hotkeys.has(normalizedKeys)) {
            console.error("Duplicated shortcut");
            return of(null);
        }

        this._hotkeys.set(normalizedKeys, mergedOptions);
        if (mergedOptions.destroyRef) {
            mergedOptions.destroyRef.onDestroy(() => {
                this.removeShortcuts(normalizedKeys);
            });
        }
        const event = `${mergedOptions.trigger}.${normalizedKeys}`;

        return new Observable<KeyboardEvent>((observer) => {
            const handler = (e: KeyboardEvent): void => {
                const hotkey = this._hotkeys.get(normalizedKeys);

                // Suppress non-dialog hotkeys when a dialog is open
                if (this._openDialogCount > 0) {
                    return;
                }

                const skipShortcutTrigger = this._targetIsExcluded(hotkey.allowIn);

                if (skipShortcutTrigger) {
                    return;
                }

                if (mergedOptions.preventDefault) {
                    e.preventDefault();
                }

                if (mergedOptions.closeAllPanels !== false) {
                    this._app.closeAllPanels();
                }

                this._callbacks.forEach((cb) => cb(e, normalizedKeys, hotkey.element));
                observer.next(e);
            };
            const dispose = this._eventManager.addEventListener(mergedOptions.element, event, handler) as () => void;

            return () => {
                this._hotkeys.delete(normalizedKeys);
                dispose();
            };
        }).pipe(takeUntil(this._dispose$.pipe(filter((v) => v === normalizedKeys))));
    }

    /**
     * Registers a single hotkey for use within a dialog.
     * Dialog hotkeys are tracked separately from non-dialog hotkeys so that the
     * same key combination can exist in both a page-level component and a dialog
     * without triggering a duplicate-hotkey error.
     * Only active when the owning dialog is the topmost dialog.
     * Auto-deregistered when the dialog closes or DestroyRef fires, whichever comes first.
     *
     * @param options - Hotkey configuration (destroyRef required)
     * @returns Observable that emits keyboard events when hotkey is triggered
     */
    addDialogShortcut$(options: Hotkey): Observable<KeyboardEvent> {
        const mergedOptions = {
            ...this._defaults,
            ...options
        };
        const normalizedKeys = this._normalizeKeys(mergedOptions.keys, this.hostPlatform());
        const currentDialogs = this._matDialog.openDialogs;
        const currentDialog = currentDialogs.length > 0 ? currentDialogs[currentDialogs.length - 1] : undefined;

        if (this._dialogHotkeys.has(normalizedKeys)) {
            console.error("Duplicated shortcut");
            return of(null);
        }

        this._dialogHotkeys.set(normalizedKeys, {
            hotkey: mergedOptions,
            dialogRef: currentDialog
        });
        if (currentDialog) {
            this._dialogOwners.set(normalizedKeys, currentDialog);
        }
        if (mergedOptions.destroyRef) {
            mergedOptions.destroyRef.onDestroy(() => {
                this._removeDialogShortcuts(normalizedKeys);
            });
        }
        const event = `${mergedOptions.trigger}.${normalizedKeys}`;

        return new Observable<KeyboardEvent>((observer) => {
            const handler = (e: KeyboardEvent): void => {
                const entry = this._dialogHotkeys.get(normalizedKeys);
                if (!entry) {
                    return;
                }

                // Suppress dialog hotkeys from a different dialog when nested
                if (entry.dialogRef) {
                    const openDialogs = this._matDialog.openDialogs;
                    if (openDialogs.length > 0 && openDialogs[openDialogs.length - 1] !== entry.dialogRef) {
                        return;
                    }
                }

                if (this._targetIsExcluded(entry.hotkey.allowIn)) {
                    return;
                }

                if (mergedOptions.preventDefault) {
                    e.preventDefault();
                }

                if (mergedOptions.closeAllPanels !== false) {
                    this._app.closeAllPanels();
                }

                this._callbacks.forEach((cb) => cb(e, normalizedKeys, entry.hotkey.element));
                observer.next(e);
            };
            const dispose = this._eventManager.addEventListener(mergedOptions.element, event, handler) as () => void;

            return () => {
                this._dialogHotkeys.delete(normalizedKeys);
                dispose();
            };
        }).pipe(takeUntil(this._dialogDispose$.pipe(filter((v) => v === normalizedKeys))));
    }

    /**
     * Registers a multi-key sequence hotkey for use within a dialog.
     * Dialog sequence hotkeys are tracked separately from non-dialog sequences so
     * that the same key sequence can exist in both a page-level component and a
     * dialog without triggering a duplicate-hotkey error.
     * Only active when the owning dialog is the topmost dialog.
     * Auto-deregistered when the dialog closes or DestroyRef fires, whichever comes first.
     *
     * @param options - Hotkey configuration with sequence keys (destroyRef required)
     * @returns Observable that emits when the sequence is completed
     */
    addDialogSequenceShortcut$(options: Hotkey): Observable<Hotkey> {
        const mergedOptions = {
            ...this._defaults,
            ...options
        };
        const normalizedKeys = this._normalizeKeys(mergedOptions.keys, this.hostPlatform());
        const currentDialogs = this._matDialog.openDialogs;
        const currentDialog = currentDialogs.length > 0 ? currentDialogs[currentDialogs.length - 1] : undefined;
        if (currentDialog) {
            this._dialogOwners.set(normalizedKeys, currentDialog);
        }

        const getDialogSequenceObserver = (element: HTMLElement, eventName: string): Observable<Hotkey> => {
            let sequence = "";
            return fromEvent<KeyboardEvent>(element, eventName).pipe(
                tap(
                    (e) =>
                        (sequence = `${sequence}${sequence ? ">" : ""}${e.ctrlKey ? "control." : ""}${e.altKey ? "alt." : ""}${
                            e.shiftKey ? "shift." : ""
                        }${e.key}`),
                ),
                debounceTime(this._sequenceDebounce),
                mergeMap(() => {
                    const resultSequence = sequence;
                    sequence = "";
                    const summary = this._dialogSequenceMaps.get(element);
                    if (summary?.hotkeyMap.has(resultSequence)) {
                        const hotkeySummary = summary.hotkeyMap.get(resultSequence);
                        // Suppress dialog hotkeys from a different dialog when nested
                        if (currentDialog) {
                            const openDialogs = this._matDialog.openDialogs;
                            if (openDialogs.length > 0 && openDialogs[openDialogs.length - 1] !== currentDialog) {
                                return EMPTY;
                            }
                        }
                        if (mergedOptions.closeAllPanels !== false) {
                            this._app.closeAllPanels();
                        }
                        hotkeySummary.subject$.next(hotkeySummary.hotkey);
                        return of(hotkeySummary.hotkey);
                    }
                    else {
                        return EMPTY;
                    }
                }),
            );
        };

        const getDialogSequenceCompleteObserver = (): Observable<Hotkey> => {
            const hotkeySummary = {
                subject$: new Subject<Hotkey>(),
                hotkey: mergedOptions
            };

            if (this._dialogSequenceMaps.has(mergedOptions.element)) {
                const sequenceSummary = this._dialogSequenceMaps.get(mergedOptions.element);

                if (sequenceSummary.hotkeyMap.has(normalizedKeys)) {
                    console.error("Duplicated shortcut");
                    return of(null);
                }

                sequenceSummary.hotkeyMap.set(normalizedKeys, hotkeySummary);
            }
            else {
                const observer$ = getDialogSequenceObserver(mergedOptions.element, mergedOptions.trigger);
                const subscription = observer$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe();

                const hotkeyMap = new Map<string, HotkeySummary>([[normalizedKeys, hotkeySummary]]);
                const sequenceSummary = {
                    subscription,
                    observer$,
                    hotkeyMap
                };
                this._dialogSequenceMaps.set(mergedOptions.element, sequenceSummary);
            }

            if (mergedOptions.destroyRef) {
                mergedOptions.destroyRef.onDestroy(() => {
                    this._removeDialogShortcuts(normalizedKeys);
                });
            }
            return hotkeySummary.subject$.asObservable();
        };

        return getDialogSequenceCompleteObserver().pipe(
            filter((hotkey) => !this._targetIsExcluded(hotkey.allowIn)),
            tap((hotkey) => this._callbacks.forEach((cb) => cb(hotkey, normalizedKeys, hotkey.element))),
            takeUntil<Hotkey>(this._dialogDispose$.pipe(filter((v) => v === normalizedKeys))),
            finalize(() => this._removeDialogShortcuts(normalizedKeys)),
        );
    }

    /**
     * Removes one or more registered hotkeys.
     *
     * Cleans up event listeners and disposes of observables for the specified hotkeys.
     * Handles both single hotkeys and sequence hotkeys.
     *
     * @param hotkeys - Single hotkey string or array of hotkey strings to remove
     *
     * @example
     * ```typescript
     * // Remove single hotkey
     * this.hotkeysService.removeShortcuts('ctrl.s');
     *
     * // Remove multiple hotkeys
     * this.hotkeysService.removeShortcuts(['ctrl.s', 'alt.f4']);
     * ```
     */
    removeShortcuts(hotkeys: string | Array<string>): void {
        const coercedHotkeys = this._coerceArray(hotkeys).map((hotkey: string) => this._normalizeKeys(hotkey, this.hostPlatform()));
        coercedHotkeys.forEach((hotkey) => {
            this._hotkeys.delete(hotkey);
            this._dialogHotkeys.delete(hotkey);
            this._dialogOwners.delete(hotkey);
            this._dispose$.next(hotkey);
            this._dialogDispose$.next(hotkey);

            [this._sequenceMaps, this._dialogSequenceMaps].forEach((seqMaps) => {
                seqMaps.forEach((v, k) => {
                    const summary = v.hotkeyMap.get(hotkey);
                    if (summary) {
                        summary.subject$.complete();
                        v.hotkeyMap.delete(hotkey);
                    }
                    if (v.hotkeyMap.size === 0) {
                        v.subscription.unsubscribe();
                        seqMaps.delete(k);
                    }
                });
            });
        });
    }

    /**
     * Sets the debounce time for key sequence detection.
     *
     * @param debounce - Debounce time in milliseconds (default: 250ms)
     *
     * @example
     * ```typescript
     * // Set longer debounce for slower typists
     * this.hotkeysService.setSequenceDebounce(500);
     * ```
     */
    setSequenceDebounce(debounce: number): void {
        this._sequenceDebounce = debounce;
    }

    /**
     * Registers a global callback that executes for all hotkey triggers.
     *
     * @param callback - Function to execute when any hotkey is triggered
     * @returns Function to unregister the callback
     *
     * @example
     * ```typescript
     * // Log all hotkey usage
     * const unregister = this.hotkeysService.onShortcut(
     *   (event, keys, target) => console.log(`Hotkey ${keys} triggered`)
     * );
     *
     * // Later, unregister the callback
     * unregister();
     * ```
     */
    onShortcut(callback: HotkeyCallback): () => void {
        this._callbacks.push(callback);

        return () => (this._callbacks = this._callbacks.filter((cb) => cb !== callback));
    }

    /**
     * Registers a help modal that displays available hotkeys.
     *
     * @param openHelpModalFn - Function to open the help modal
     * @param helpShortcut - Key combination to trigger help (default: 'shift.?')
     *
     * @example
     * ```typescript
     * this.hotkeysService.registerHelpModal(
     *   () => this.dialog.open(HelpModalComponent),
     *   'ctrl.h'
     * );
     * ```
     */
    registerHelpModal(openHelpModalFn: () => void, helpShortcut: string = ""): void {
        this.addShortcut$({
            keys: helpShortcut || "shift.?",
            showInHelpMenu: false,
            preventDefault: false
        }).pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(
                (e) => {
                    const skipMenu
                        = /^(input|textarea|select)$/i.test(document.activeElement.nodeName)
                            || (e.target as HTMLElement).isContentEditable;

                    if (!skipMenu && this._hotkeys.size) {
                        openHelpModalFn();
                    }
                },
            );
    }

    /**
     * Detects the current platform for key normalization.
     *
     * @returns Platform enum value (Apple or PC)
     */
    hostPlatform(): Platform {

        const appleDevices = ["Mac", "iPhone", "iPad"];
        return appleDevices.some((d) => navigator.userAgent.includes(d)) ? Platform.Apple : Platform.PC;
    }

    /**
     * Removes dialog-scoped hotkeys without affecting non-dialog hotkeys that share the same key.
     *
     * @param hotkeys - Single hotkey string or array of hotkey strings to remove
     */
    private _removeDialogShortcuts(hotkeys: string | Array<string>): void {
        const coercedHotkeys = this._coerceArray(hotkeys).map((hotkey: string) => this._normalizeKeys(hotkey, this.hostPlatform()));
        coercedHotkeys.forEach((hotkey) => {
            this._dialogHotkeys.delete(hotkey);
            this._dialogOwners.delete(hotkey);
            this._dialogDispose$.next(hotkey);

            this._dialogSequenceMaps.forEach((v, k) => {
                const summary = v.hotkeyMap.get(hotkey);
                if (summary) {
                    summary.subject$.complete();
                    v.hotkeyMap.delete(hotkey);
                }
                if (v.hotkeyMap.size === 0) {
                    v.subscription.unsubscribe();
                    this._dialogSequenceMaps.delete(k);
                }
            });
        });
    }

    /**
     * Determines if the current active element should exclude hotkey triggers.
     *
     * By default, hotkeys are disabled in input fields, textareas, select elements,
     * and contenteditable elements unless explicitly allowed.
     *
     * @param allowIn - Array of element types where hotkeys should be allowed
     * @returns True if hotkeys should be excluded for the current active element
     */
    private _targetIsExcluded(allowIn?: Array<AllowInElement>): boolean {
        const activeElement = this._document.activeElement;
        const elementName = activeElement.nodeName;
        const elementIsContentEditable = (activeElement as HTMLElement).isContentEditable;
        let isExcluded = ["INPUT", "SELECT", "TEXTAREA"].includes(elementName) || elementIsContentEditable;

        if (isExcluded && allowIn?.length) {
            for (const t of allowIn) {
                if (activeElement.nodeName === t || (t === "CONTENTEDITABLE" && elementIsContentEditable)) {
                    isExcluded = false;
                    break;
                }
            }
        }

        return isExcluded;
    }

    /**
     * Normalizes key combinations for cross-platform compatibility.
     *
     * Handles platform-specific differences (e.g., meta vs control keys)
     * and converts arrow key aliases to standard names.
     *
     * @param keys - Raw key combination string
     * @param platform - Target platform for normalization
     * @returns Normalized key combination string
     */
    private _normalizeKeys(keys: string, platform: Platform): string {

        const transform = (key: string): string => {

            const transformMap = {
                up: "ArrowUp",
                down: "ArrowDown",
                left: "ArrowLeft",
                right: "ArrowRight"
            };
            if (platform === Platform.PC && key === "meta") {
                key = "control";
            }

            if (key in transformMap) {
                key = transformMap[key as keyof typeof transformMap];
            }

            return key;
        };

        return keys.toLowerCase()
            .split(">")
            .map((s) => s.split(".").map(transform)
                .join("."))
            .join(">");
    }

    /**
     * Converts a single value or array to an array.
     *
     * @param params - Single value or array to coerce
     * @returns Array containing the input value(s)
     */
    private _coerceArray<T>(params: T | Array<T>): Array<T> {

        return Array.isArray(params) ? params : [params];
    }
}
